/**
 * Authentication Context - Supabase Auth
 *
 * Uses Supabase Auth for:
 * - Sign up / Sign in (email + password)
 * - Session management (auto-refresh, persistence)
 * - Password reset
 *
 * User profile data is fetched from the `users` table and linked to `auth.users`
 * via the user's ID.
 */
import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useCallback,
  useRef,
  type ReactNode,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import type { Session, User as AuthUser } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import type { Row } from '../lib/db.types';

const PROFILE_CACHE_KEY = 'userlife-profile';

export type UserProfile =
  | Row<'users'>
  | {
      id: string;
      email: string | null;
      tenant_id: string | null;
      role_id: string | null;
    };

export interface AuthResult<T = unknown> {
  success: boolean;
  error?: string;
  data?: T;
}

export interface RegisterInput {
  tenant_name: string;
  tenant_slug: string;
  email: string;
  password: string;
}

export interface RegisterPendingInput {
  company_name: string;
  first_name: string;
  last_name: string;
  email: string;
  password: string;
}

export interface AuthContextValue {
  user: UserProfile | null;
  session: Session | null;
  sessionReady: boolean;
  loading: boolean;
  isAuthenticated: boolean;
  register: (input: RegisterInput) => Promise<AuthResult>;
  registerPending: (input: RegisterPendingInput) => Promise<AuthResult>;
  login: (email: string, password: string) => Promise<AuthResult>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<AuthResult>;
  updatePassword: (newPassword: string) => Promise<AuthResult>;
  /** Direct Supabase client for ad-hoc queries when a helper isn't available. */
  supabase: typeof supabase;
}

const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<UserProfile | null>(() => {
    try {
      const cached = localStorage.getItem(PROFILE_CACHE_KEY);
      return cached ? (JSON.parse(cached) as UserProfile) : null;
    } catch {
      return null;
    }
  });
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [sessionReady, setSessionReady] = useState<boolean>(false);
  const navigate = useNavigate();
  const initDone = useRef<boolean>(false);

  const fetchUserProfile = useCallback(
    async (authUser: AuthUser | null): Promise<UserProfile | null> => {
      if (!authUser) return null;
      try {
        const { data, error } = await supabase
          .from('users')
          .select('*')
          .eq('id', authUser.id)
          .single();

        if (error) {
          // eslint-disable-next-line no-console
          console.warn('Profile fetch warning:', error.message);
          const meta = (authUser.user_metadata ?? {}) as Record<string, unknown>;
          return {
            id: authUser.id,
            email: authUser.email ?? null,
            tenant_id: (meta['tenant_id'] as string) ?? null,
            role_id: (meta['role_id'] as string) ?? null,
          };
        }
        return data;
      } catch (err: unknown) {
        // eslint-disable-next-line no-console
        console.error('Profile fetch error:', err);
        const meta = (authUser.user_metadata ?? {}) as Record<string, unknown>;
        return {
          id: authUser.id,
          email: authUser.email ?? null,
          tenant_id: (meta['tenant_id'] as string) ?? null,
          role_id: (meta['role_id'] as string) ?? null,
        };
      }
    },
    []
  );

  useEffect(() => {
    let mounted = true;

    const init = async (): Promise<void> => {
      try {
        const {
          data: { session: s },
        } = await supabase.auth.getSession();
        if (!mounted) return;
        initDone.current = true;
        setSession(s);
        if (s) setSessionReady(true);

        if (s?.user) {
          const profile = await fetchUserProfile(s.user);
          if (mounted && profile) {
            setUser(profile);
            localStorage.setItem(PROFILE_CACHE_KEY, JSON.stringify(profile));
          }
        } else {
          setUser(null);
          localStorage.removeItem(PROFILE_CACHE_KEY);
        }
      } catch (err: unknown) {
        // eslint-disable-next-line no-console
        console.error('Auth init error:', err);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    void init();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (_event, s) => {
      if (!mounted || !initDone.current) return;
      setSession(s);
      if (s) setSessionReady(true);

      if (s?.user) {
        const profile = await fetchUserProfile(s.user);
        if (mounted && profile) {
          setUser(profile);
          localStorage.setItem(PROFILE_CACHE_KEY, JSON.stringify(profile));
        }
      } else {
        setUser(null);
        localStorage.removeItem(PROFILE_CACHE_KEY);
      }
    });

    const timeout = setTimeout(() => {
      if (mounted) setLoading(false);
    }, 4000);

    return () => {
      mounted = false;
      clearTimeout(timeout);
      subscription.unsubscribe();
    };
  }, [fetchUserProfile]);

  const register: AuthContextValue['register'] = async ({
    tenant_name,
    tenant_slug,
    email,
    password,
  }) => {
    try {
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: { data: { tenant_name, tenant_slug } },
      });
      if (error) throw error;
      toast.success('Account created successfully!', { description: 'Welcome to UserLife' });
      return { success: true };
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Registration failed';
      toast.error('Registration Failed', { description: message });
      return { success: false, error: message };
    }
  };

  const registerPending: AuthContextValue['registerPending'] = async ({
    company_name,
    first_name,
    last_name,
    email,
    password,
  }) => {
    try {
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            company_name,
            first_name,
            last_name,
            pending_approval: true,
          },
        },
      });
      if (error) throw error;

      // Note: pending_registrations.password_hash is NOT NULL — store a placeholder
      // since Supabase Auth owns the real credential.
      await supabase.from('pending_registrations').insert({
        email,
        company_name,
        first_name,
        last_name,
        password_hash: '__supabase_auth__',
        status: 'pending',
      });

      return { success: true };
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Registration failed';
      toast.error('Registration Failed', { description: message });
      return { success: false, error: message };
    }
  };

  const login: AuthContextValue['login'] = async (email, password) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
      toast.success('Login successful!', { description: 'Welcome back' });
      return { success: true, data };
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Login failed';
      toast.error('Login Failed', { description: message });
      return { success: false, error: message };
    }
  };

  const logout: AuthContextValue['logout'] = async () => {
    try {
      await supabase.auth.signOut();
    } catch (err: unknown) {
      // eslint-disable-next-line no-console
      console.error('Logout error:', err);
    } finally {
      setUser(null);
      setSession(null);
      setSessionReady(false);
      localStorage.removeItem(PROFILE_CACHE_KEY);
      toast.success('Logged out successfully');
      navigate('/app/login');
    }
  };

  const resetPassword: AuthContextValue['resetPassword'] = async (email) => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/app/reset-password`,
      });
      if (error) throw error;
      return { success: true };
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Reset failed';
      return { success: false, error: message };
    }
  };

  const updatePassword: AuthContextValue['updatePassword'] = async (newPassword) => {
    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) throw error;
      return { success: true };
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Update failed';
      return { success: false, error: message };
    }
  };

  const value: AuthContextValue = {
    user,
    session,
    sessionReady,
    loading,
    isAuthenticated: !!session && !!user,
    register,
    registerPending,
    login,
    logout,
    resetPassword,
    updatePassword,
    supabase,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
